/**
 * Override Service (W25 — constraint-aware UI, lean override slice).
 *
 * Thin client over the single-actor override CRUD endpoints (W24). An override
 * lets a student record a justified, evidenced deviation that is active on
 * creation and flips a requirement's effective status (honestly — the raw
 * verdict stays visible).
 */

import { typedApi } from './typedApiClient';

export type OverrideStatus = 'active' | 'expired' | 'revoked';

export type OverrideScope =
  | 'plan'
  | 'requirement'
  | 'course_in_requirement'
  | 'prereq_edge'
  | 'substitution'
  | 'ap_credit_exception';

export type EvidenceType =
  | 'placement_test'
  | 'transfer_credit'
  | 'advisor_email'
  | 'department_letter'
  | 'instructor_consent'
  | 'self_declaration';

export interface Override {
  id: number;
  semantic_id: string;
  plan_id: number;
  constraint_id: string;
  scope: OverrideScope;
  target_ref: string | null;
  justification: string;
  evidence_type: EvidenceType;
  evidence_ref: string | null;
  requested_by: string;
  created_at: string;
  expires_at: string | null;
  status: OverrideStatus;
  institution_id: number;
}

export interface OverrideDraft {
  constraint_id: string;
  scope: OverrideScope;
  justification: string;
  evidence_type: EvidenceType;
  target_ref?: string | null;
  evidence_ref?: string | null;
  expires_at?: string | null;
}

class OverrideService {
  /** List a plan's overrides, optionally filtered by status / constraint. */
  async list(
    planId: number,
    opts?: { status?: OverrideStatus; constraintId?: string }
  ): Promise<Override[]> {
    const data = await typedApi.overrides.list(planId, {
      ...(opts?.status ? { status: opts.status } : {}),
      ...(opts?.constraintId ? { constraint_id: opts.constraintId } : {}),
    });
    return (data?.overrides ?? []) as Override[];
  }

  /** Create an override (active on creation, single-actor). */
  async create(planId: number, draft: OverrideDraft): Promise<Override> {
    return (await typedApi.overrides.create(planId, draft)) as Override;
  }

  /** Revoke an override (the student undoes their own deviation). */
  async revoke(planId: number, overrideId: number, reason: string): Promise<Override> {
    return (await typedApi.overrides.revoke(planId, overrideId, { reason })) as Override;
  }
}

export const overrideService = new OverrideService();
export default overrideService;
